import React, { useRef } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ImageSkeleton from '../PlaceholderSkeleton/ImageSkeleton';
import { useScreenshotTimelineQuery } from '../../queries/screenshots';
import { screenshotRelativeTime, screenshotUtcTime } from './screenshotUtils';

interface ScreenshotThumbProps {
  threatId?: number;
  /** Width of the image; the WebP thumbnail is 320x180. */
  width?: number | string;
  /** Skeleton height while the timeline loads. */
  skeletonHeight?: number;
  /** Prefix for the data-testids (`<testId>`, `<testId>-empty`). */
  testId?: string;
  /** Skip fetching (e.g. a collapsed row). */
  enabled?: boolean;
}

/**
 * Latest stored thumbnail for a domain, with its relative capture time. Used
 * in list surfaces (expanded rows, preview panel, card) - no strip, no
 * capture control; those live on the detail page (WHEEL-394).
 */
const ScreenshotThumb = ({
  threatId,
  width = 200,
  skeletonHeight = 112,
  testId = 'screenshot-thumb',
  enabled = true,
}: ScreenshotThumbProps) => {
  const { data, isPending, refetch } = useScreenshotTimelineQuery(threatId, {
    limit: 1,
    enabled,
  });
  const retriedRef = useRef(false);

  const latest = data?.items?.[0];
  const loading = enabled && typeof threatId === 'number' && isPending;

  if (loading) {
    return <ImageSkeleton height={skeletonHeight} width={width} />;
  }

  // No entries, a 404 (domain unknown to the service) or any other error all
  // render the same placeholder - there is nothing actionable in a row.
  if (!latest) {
    return (
      <Box
        data-testid={`${testId}-empty`}
        sx={{ width, display: 'flex', flexDirection: 'column', gap: 0.5 }}
      >
        <Box
          sx={{
            width: '100%',
            aspectRatio: '16 / 9',
            borderRadius: '8px',
            border: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ImageOutlinedIcon
            aria-label="No screenshot available"
            sx={{ fontSize: 32, color: 'text.disabled' }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          No screenshot yet
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box
        component="img"
        src={latest.thumbnail_url}
        alt="Screenshot thumbnail"
        data-testid={testId}
        onError={() => {
          // A signed URL that expired under us: refetch once for fresh URLs.
          if (retriedRef.current) return;
          retriedRef.current = true;
          refetch();
        }}
        sx={{
          width: '100%',
          height: 'auto',
          borderRadius: '8px',
          border: 1,
          borderColor: 'divider',
          display: 'block',
        }}
      />
      <Tooltip
        title={screenshotUtcTime(latest.created)}
        placement="bottom-start"
      >
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid={`${testId}-caption`}
          sx={{ cursor: 'default', width: 'fit-content' }}
        >
          {screenshotRelativeTime(latest.created) || 'Capture time unknown'}
        </Typography>
      </Tooltip>
    </Box>
  );
};

export default ScreenshotThumb;
